import config from "./config";
import { getPartnerRoleTypesForInsights } from "./rechargeSalesInsights";

/** POST paths (relative to api base) — proxied by partner-analytics-api. */
export const SIM_ACT_METRICS_ENDPOINTS = {
  daily: "pams-insights/sim-act-metrics/daily-trends",
  weekly: "pams-insights/sim-act-metrics/weekly-trends",
  monthly: "pams-insights/sim-act-metrics/monthly-trends",
  mtdLmtd: "pams-insights/sim-act-metrics/trends-mtd-lmtd",
};

export const MDN_SECTION = "MDN Sim Connectivity";
export const SIM_BILLING_SECTION = "Sim Billing";

export const TILE_SIM_ACTIVATION = "SIM Activation";
export const TILE_DAILY_ACTIVATING_OUTLET = "Daily Activating Outlet";

export const SIM_BILLING_TIER_OPTIONS = [
  { key: "PRIMARY", label: "Primary SIM Sales" },
  { key: "SECONDARY", label: "Secondary SIM Sales" },
  { key: "TERTIARY", label: "Tertiary SIM Sales" },
];

/**
 * Sim Billing tiers supported for a partner role (PAMS role×tier matrix).
 * e.g. RDS has PRIMARY = NA → only Secondary / Tertiary.
 * Falls back to all tiers when the role has no known mapping.
 */
export function getAvailableSimBillingTiers(partnerRoleName) {
  const available = SIM_BILLING_TIER_OPTIONS.filter(
    (tier) =>
      getPartnerRoleTypesForInsights(partnerRoleName, tier.key).length > 0
  );
  return available.length > 0 ? available : SIM_BILLING_TIER_OPTIONS;
}

export const MDN_ACTIVATION_TYPE_OPTIONS = [
  { key: "Jiophone", label: "Jiophone" },
  { key: "SmartPhone", label: "SmartPhone" },
  { key: "MNP", label: "MNP" },
  { key: "Postpaid", label: "Postpaid" },
];

/** Default when Daily Activating Outlet has no activation-type pills (per BRD). */
export const DEFAULT_OUTLET_ACTIVATION_TYPE = "SmartPhone";

function partnerIdFromFilter(filterValue) {
  if (!filterValue) return "";
  if (filterValue.orgId && filterValue.orgId !== "") {
    return String(filterValue.orgId);
  }
  const role = filterValue.selectedRole;
  if (role && String(role).includes(" - ")) {
    const parts = String(role).split(" - ");
    return String(parts[2] || "");
  }
  return "";
}

function jprTypeNameFromFilter(filterValue) {
  if (
    !filterValue ||
    !filterValue.selectedCategory ||
    filterValue.selectedCategory === "ALL"
  ) {
    return "";
  }
  return String(filterValue.selectedCategory);
}

/**
 * Builds POST body for sim-act-metrics (Swagger: partner_role_type singular).
 */
export function buildSimActMetricsPayload(
  filterValue,
  partnerRoleTypeShort,
  {
    moduleName,
    section,
    tile,
    activationType,
    metricType,
  }
) {
  const base = {
    partner_id: partnerIdFromFilter(filterValue),
    partner_role_type: partnerRoleTypeShort ? String(partnerRoleTypeShort) : "",
    module_name: moduleName || "Sim Activation",
    section: section || "",
    tile: tile || "",
    activation_type: activationType != null ? String(activationType) : "",
    jpr_type_name: jprTypeNameFromFilter(filterValue),
  }; 
 
  switch (metricType) {
    case "daily":
      return { ...base, days: 30 };
    case "weekly":
      return { ...base, weeks: 12 };
    case "monthly":
      return { ...base, months: 3 };
    case "mtdLmtd":
      return base;
    default:
      return base;
  }
}

export function simBillingTileLabel(tierKey) {
  const m = {
    PRIMARY: "Primary SIM Sales",
    SECONDARY: "Secondary SIM Sales",
    TERTIARY: "Tertiary SIM Sales",
  };
  return m[tierKey] || "Primary SIM Sales";
}

export function metricUrl(metricKey) {
  const path = SIM_ACT_METRICS_ENDPOINTS[metricKey];
  return path ? `${config.apiBasePath.baseIp}${path}` : "";
}

export { INSIGHTS_METRIC_TYPES } from "./rechargeSalesInsights";

/** Maps Redux keys (e.g. SB_PRIMARY_daily) to RechargeSalesInsightsView keys (PRIMARY_daily). */
export function stripPrefixFromKeys(obj, prefix) {
  if (!obj || typeof obj !== "object") return {};
  if (!prefix) return { ...obj };
  const out = {};
  Object.keys(obj).forEach((k) => {
    if (k.startsWith(prefix)) {
      out[k.slice(prefix.length)] = obj[k];
    }
  });
  return out;
}

/** Keys like ACT_Jiophone_daily → Jiophone_daily; OUTLET_daily kept as-is. */
export function mapSimConnectivityInsightKeys(data, loadingKeys, mode) {
  const outData = {};
  const outLoading = {};
  const pick = (src, dest) => {
    if (!src || typeof src !== "object") return;
    Object.keys(src).forEach((k) => {
      if (mode === "simActivation" && k.startsWith("ACT_")) {
        dest[k.slice(4)] = src[k];
      } else if (mode === "dailyOutlet" && k.startsWith("OUTLET_")) {
        dest[k] = src[k];
      }
    });
  };
  pick(data, outData);
  pick(loadingKeys, outLoading);
  return { data: outData, loadingKeys: outLoading };
}

/**
 * Maps an API activation row (activationType + activationCount) to MDN_ACTIVATION_TYPE_OPTIONS.key.
 */
export function mapActivationRowToOptionKey(row) {
  const first = String(row?.activationType || "").split(" ")[0].trim();
  if (!first) return null;
  const lower = first.toLowerCase();
  const exact = MDN_ACTIVATION_TYPE_OPTIONS.find(
    (o) => o.key.toLowerCase() === lower
  );
  if (exact) return exact.key;
  return (
    MDN_ACTIVATION_TYPE_OPTIONS.find(
      (o) =>
        lower.startsWith(o.key.toLowerCase().slice(0, 4)) ||
        o.key.toLowerCase().startsWith(lower.slice(0, 4))
    )?.key ?? null
  );
}

/**
 * Pills for activation types with activationCount &gt; 0 only.
 * While loading or on error / empty payload, returns full list so UI is not empty.
 */
export function filterActivationTypeOptionsByCount(
  activationRows,
  jiophoneLoading,
  jiophoneError
) {
  if (jiophoneError) return MDN_ACTIVATION_TYPE_OPTIONS;
  if (!Array.isArray(activationRows) || activationRows.length === 0) {
    return MDN_ACTIVATION_TYPE_OPTIONS;
  }
  if (jiophoneLoading) return MDN_ACTIVATION_TYPE_OPTIONS;
  const withPositive = activationRows.filter(
    (r) => Number(r.activationCount) > 0
  );
  const keys = new Set(
    withPositive.map((r) => mapActivationRowToOptionKey(r)).filter(Boolean)
  );
  if (keys.size === 0) {
    if (withPositive.length > 0) {
      return MDN_ACTIVATION_TYPE_OPTIONS;
    }
    return [];
  }
  return MDN_ACTIVATION_TYPE_OPTIONS.filter((o) => keys.has(o.key));
}
